import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import {
  abaixoDaMateria,
  agruparPorMateria,
  dataCurta,
  diaDaSemanaPorExtenso,
  type PainelDaHome,
} from "@noesis/shared";
import { useAtrasado } from "../componentes/atraso.js";
import { FalhaDaApi, pedir } from "../api.js";
import { Heatmap } from "../componentes/Heatmap.js";
import { Icone } from "../componentes/Icone.js";
import { Mural } from "../componentes/Mural.js";
import { Risquinhos } from "../componentes/Risquinhos.js";
import { CaixaMao } from "../componentes/Tracos.js";
import { Semana, fraseDoComeco, fraseDoDescanso, useInicioDaConta } from "../componentes/Semana.js";
import { useRitual } from "../estado/ritual.js";
import { useSessao } from "../estado/sessao.js";
import { useTour } from "../estado/tour.js";
import { useTrilhaAtiva, useTrilhas } from "../estado/trilhas.js";
import { SemTrilha } from "./SemTrilha.js";

/**
 * Início — 7a no desktop, 13a no celular (tema Caderno, ADR 040).
 *
 * À esquerda, o que se faz hoje: a meta em risquinhos, a lista "Para hoje"
 * com visto desenhado, o botão que começa a sessão e a atividade do ano. À
 * direita, como está indo: a ofensiva num post-it, o resumo num cartão à mão,
 * a semana e as anotações rápidas (mural, RN-50).
 *
 * Nada some por largura (RN-30): no celular as mesmas peças descem numa
 * coluna, com a meta num cartão e o botão na largura inteira.
 */

type Estado =
  | { nome: "carregando" }
  | { nome: "pronto"; painel: PainelDaHome }
  | { nome: "erro"; mensagem: string };


/** A saudação pela hora no fuso do usuário (invariante 16). */
function saudacao(fuso: string): string {
  let hora = new Date().getHours();
  try {
    hora = Number(new Intl.DateTimeFormat("pt-BR", { hour: "numeric", hourCycle: "h23", timeZone: fuso }).format(new Date()));
  } catch {
    /* fuso inválido cai na hora do aparelho */
  }
  if (hora < 12) return "Bom dia";
  if (hora < 18) return "Boa tarde";
  return "Boa noite";
}

/** "quarta-feira, 23 de setembro" → "quarta, 23 de setembro", como no handoff. */
function dataDoCabecalho(hoje: string): string {
  return diaDaSemanaPorExtenso(hoje).replace("-feira", "");
}

/** Acerto no mês corrente, somado do heatmap (o painel já traz o ano). */
function acertoNoMes(p: PainelDaHome): number | null {
  const mes = p.hoje.slice(0, 7);
  let r = 0;
  let a = 0;
  for (const d of p.heatmap) {
    if (d.data.startsWith(mes)) {
      r += d.respondidos;
      a += d.acertos;
    }
  }
  return r > 0 ? Math.round((a / r) * 100) : null;
}

export function Inicio() {
  const sessao = useSessao();
  const { definir } = useRitual();
  const { estado: estadoTrilhas } = useTrilhas();
  const ativa = useTrilhaAtiva();
  const [estado, setEstado] = useState<Estado>({ nome: "carregando" });
  const mostrarEsqueleto = useAtrasado(estado.nome === "carregando");

  const carregar = useCallback(async () => {
    setEstado({ nome: "carregando" });
    try {
      setEstado({ nome: "pronto", painel: await pedir("home.painel") });
    } catch (e) {
      setEstado({ nome: "erro", mensagem: e instanceof FalhaDaApi ? e.message : "Não foi possível carregar." });
    }
  }, []);

  useEffect(() => {
    if (estadoTrilhas.nome !== "pronto") return;
    if (!estadoTrilhas.ativaId) {
      setEstado({ nome: "erro", mensagem: "sem-trilha" });
      return;
    }
    void carregar();
  }, [carregar, estadoTrilhas]);

  // A ofensiva que veio no painel é a mesma que o cabeçalho do celular mostra.
  useEffect(() => {
    if (estado.nome === "pronto") definir(estado.painel.ofensiva);
  }, [estado, definir]);

  const primeiroNome = sessao.usuario.nome.split(" ")[0] ?? sessao.usuario.nome;
  const cabecalho = (
    <header className="inicio__cabecalho">
      <p className="t-anotacao t-2">{dataDoCabecalho(sessao.hoje)}</p>
      <h1 className="t-titulo">
        {saudacao(sessao.usuario.fusoHorario)}, {primeiroNome}
      </h1>
    </header>
  );

  if (!ativa && estadoTrilhas.nome === "pronto") {
    return (
      <div className="inicio">
        {cabecalho}
        <div className="linha-vazia" />
        <SemTrilha trilhas={estadoTrilhas.trilhas} />
      </div>
    );
  }

  if (estado.nome === "carregando") {
    return (
      <div className="inicio" aria-busy="true" data-esqueleto={mostrarEsqueleto ? "visivel" : "mudo"}>
        {cabecalho}
        <span className="so-leitor" role="status">
          Carregando o painel
        </span>
        <div className="inicio__grade">
          <div className="inicio__principal">
            <div className="linha-vazia" />
            <div className="esqueleto esqueleto--bloco" />
          </div>
          <div className="inicio__lado">
            <div className="esqueleto esqueleto--bloco" />
          </div>
        </div>
      </div>
    );
  }

  if (estado.nome === "erro") {
    return (
      <div className="inicio">
        {cabecalho}
        <div className="linha-vazia" />
        <div className="estado estado--erro" role="alert">
          <h2 className="estado__titulo">Não deu para carregar o painel</h2>
          <p className="estado__texto">{estado.mensagem}</p>
          <button className="botao" type="button" onClick={() => void carregar()}>
            Tentar de novo
          </button>
        </div>
      </div>
    );
  }

  if (estado.painel.itensTotal === 0) return <TrilhaVazia p={estado.painel} cabecalho={cabecalho} />;

  return <Painel p={estado.painel} cabecalho={cabecalho} />;
}

/**
 * A trilha que acabou de nascer, sem questão nenhuma (ADR 041).
 *
 * Antes ela mostrava o painel inteiro com um "Estudar 10" que não levava a
 * lugar nenhum. Agora a folha diz o que falta — trazer questões — e tem o
 * botão que faz isso. A meta fica esmaecida, porque ainda não conta.
 */
function TrilhaVazia({ p, cabecalho }: { p: PainelDaHome; cabecalho: ReactNode }) {
  const tour = useTour();
  return (
    <div className="inicio">
      <div className="inicio__grade">
        <div className="inicio__principal">
          {cabecalho}
          <div className="linha-vazia" />

          <section className="cartao-mao inicio__vazia" aria-labelledby="vazia-titulo">
            <h2 className="t-secao" id="vazia-titulo">
              Sua trilha ainda não tem questões
            </h2>
            <p className="t-corpo t-2">
              Antes da primeira sessão, traga questões: você descreve o assunto, um modelo de IA escreve, o Grifo
              confere.
            </p>
            <div className="linha-vazia" />
            <Link
              className="botao"
              to="/acervo?aba=importar"
              data-tour="importarCta"
              onClick={() => tour.avancarSe("importarCta")}
            >
              <Icone glifo="importar" papel="acao" />
              Importar as primeiras questões
            </Link>
          </section>

          <div className="linha-vazia" />
          <section className="inicio__meta inicio__meta--apagada" aria-labelledby="meta-titulo">
            <h2 className="t-secao" id="meta-titulo">
              Meta de hoje
            </h2>
            <p className="t-corpo">Começa a contar quando houver questões.</p>
          </section>
        </div>

        <aside className="inicio__lado" aria-label="Como está indo">
          <div className="postit postit--direita inicio__ofensiva">
            <p className="inicio__ofensiva-numero">
              {p.ofensiva.atual} {p.ofensiva.atual === 1 ? "dia" : "dias"}
            </p>
            <p className="inicio__ofensiva-texto">
              {p.ofensiva.atual === 1 ? "seguido" : "seguidos"}, recorde {p.ofensiva.recorde}
            </p>
          </div>

          <section className="cartao-mao inicio__resumo" aria-labelledby="resumo-titulo">
            <h2 className="t-secao" id="resumo-titulo">
              Resumo
            </h2>
            <dl className="resumo">
              <div className="resumo__linha">
                <dt className="t-ajuda">Itens na trilha</dt>
                <dd className="t-secao">0</dd>
              </div>
              <div className="resumo__linha">
                <dt className="t-ajuda">Até a prova</dt>
                <dd className="t-secao">{ateAProva(p.diasAteAlvo)}</dd>
              </div>
            </dl>
          </section>

          <section className="inicio__semana" aria-labelledby="semana-titulo">
            <h2 className="t-secao" id="semana-titulo">
              Esta semana
            </h2>
            <Semana painel={p} />
            <ComecoDaConta p={p} />
          </section>
        </aside>
      </div>
    </div>
  );
}

/**
 * Na semana em que a conta nasceu, a fileira tem pontos neutros: a frase diz
 * por quê, e lembra quais dias são descanso (ADR 041).
 */
function ComecoDaConta({ p }: { p: PainelDaHome }) {
  const comeco = fraseDoComeco(p, useInicioDaConta());
  if (!comeco) return null;
  const descanso = fraseDoDescanso(p.diasDeDescanso);
  return <p className="t-ajuda t-2">{[comeco, descanso].filter(Boolean).join(" ")}</p>;
}

function ateAProva(dias: number | null): string {
  if (dias === null) return "sem data";
  if (dias > 0) return `${dias} ${dias === 1 ? "dia" : "dias"}`;
  return dias === 0 ? "hoje" : "passou";
}

function Painel({ p, cabecalho }: { p: PainelDaHome; cabecalho: ReactNode }) {
  const navegar = useNavigate();
  const [montando, setMontando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const acerto = useMemo(() => acertoNoMes(p), [p]);

  const faltam = Math.max(p.metaDiaria - p.respondidosHoje, 0);
  const cumpriu = faltam === 0;
  const bonus = Math.max(p.respondidosHoje - p.metaDiaria, 0);
  // "Estudar mais N": o que falta para a meta, ou uma meta inteira depois dela.
  const quantos = cumpriu ? p.metaDiaria : faltam;
  const semNovas = p.itensVistos >= p.itensTotal;

  async function estudar() {
    setMontando(true);
    setErro(null);
    try {
      const r = await pedir("estudo.montar", { corpo: { quantidade: quantos } });
      if (!r.montada && !r.atual.sessao) {
        setErro("Nenhum item disponível para uma sessão agora.");
        return;
      }
      navegar("/estudar");
    } catch (e) {
      setErro(e instanceof FalhaDaApi ? e.message : "Não foi possível começar a sessão.");
    } finally {
      setMontando(false);
    }
  }

  const frase = p.emFerias
    ? `Em férias desde ${p.feriasDesde ? dataCurta(p.feriasDesde) : ""}. Estes dias não contam como falha nem como cumprimento.`
    : cumpriu
      ? bonus > 0
        ? "Meta cumprida, o último risco é bônus."
        : "Meta cumprida."
      : p.respondidosHoje === 0
        ? `Faltam ${faltam} para a meta de hoje.`
        : `Faltam ${faltam}.`;

  const tarefas = [
    {
      chave: "revisar",
      feito: p.vencemHoje === 0,
      texto:
        p.vencemHoje === 0 ? (
          <>Nada vence hoje para revisar</>
        ) : (
          <>
            Revisar as <b>{p.vencemHoje}</b> que vencem hoje
          </>
        ),
      acao: p.vencemHoje > 0 ? { rotulo: "revisar", para: "/revisar?aba=devidas" } : null,
    },
    {
      chave: "erradas",
      feito: p.erradasPendentes === 0,
      texto:
        p.erradasPendentes === 0 ? (
          <>Nenhuma errada esperando</>
        ) : (
          <>
            Refazer as <b>{p.erradasPendentes}</b> erradas
          </>
        ),
      acao: p.erradasPendentes > 0 ? { rotulo: "praticar", para: "/revisar" } : null,
    },
    semNovas
      ? {
          chave: "novas",
          feito: false,
          texto: <>Sem questões novas na trilha</>,
          acao: { rotulo: "importar mais", para: "/acervo?aba=importar" },
        }
      : {
          chave: "novas",
          feito: cumpriu,
          texto: (
            <>
              Fazer {p.metaDiaria} {p.metaDiaria === 1 ? "questão" : "questões"} {cumpriu ? "hoje" : "novas"}
            </>
          ),
          acao: null,
        },
  ];

  return (
    <div className="inicio">
      <div className="inicio__grade">
        <div className="inicio__principal">
          {cabecalho}
          <div className="linha-vazia" />

          <section className="inicio__meta" aria-labelledby="meta-titulo">
            <h2 className="t-secao inicio__meta-titulo" id="meta-titulo">
              Meta de hoje
            </h2>
            <div className="inicio__meta-linha">
              <span className="inicio__meta-numero t-numero" aria-hidden="true">
                {p.respondidosHoje}
                <span className="t-anotacao"> de {p.metaDiaria}</span>
              </span>
              <Risquinhos
                quantidade={p.respondidosHoje}
                destaque={Math.max(0, Math.min(p.respondidosHoje, p.metaDiaria + 5) - p.metaDiaria)}
                maximo={p.metaDiaria + 5}
                rotulo={`${p.respondidosHoje} de ${p.metaDiaria}`}
              />
              <p className="t-corpo inicio__meta-frase" role="status">
                <b className="inicio__meta-conta">
                  {p.respondidosHoje} de {p.metaDiaria}.
                </b>{" "}
                {frase}
              </p>
            </div>
          </section>

          <section className="inicio__hoje" aria-labelledby="hoje-titulo">
            <h2 className="t-secao" id="hoje-titulo">
              Para hoje
            </h2>
            <ul className="lista-hoje">
              {tarefas.map((t) => (
                <li key={t.chave} className={t.feito ? "lista-hoje__item lista-hoje__item--feito" : "lista-hoje__item"}>
                  <CaixaMao marcada={t.feito} verde />
                  <span className="t-corpo lista-hoje__texto">
                    {t.texto}
                    {t.feito && <span className="so-leitor"> (feito)</span>}
                  </span>
                  {t.acao && (
                    <Link className="link-mao lista-hoje__acao" to={t.acao.para}>
                      {t.acao.rotulo}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </section>

          <div className="inicio__acoes">
            <button type="button" className="botao inicio__estudar" disabled={montando} onClick={() => void estudar()}>
              <Icone glifo="play" papel="acao" className="inicio__estudar-icone" />
              {montando ? "Montando…" : cumpriu ? `Estudar mais ${quantos}` : `Estudar ${quantos}`}
            </button>
            <Link className="link-mao link-mao--tinta inicio__filtros" to="/estudar">
              ou montar com filtros
            </Link>
          </div>
          {erro && (
            <p className="campo__erro" role="alert">
              {erro}
            </p>
          )}

          <section className="inicio__atividade" aria-label="Atividade">
            <Heatmap dias={p.heatmap} de={p.heatmapDe} hoje={p.hoje} meta={p.metaDiaria} diasDeDescanso={p.diasDeDescanso} />
          </section>
        </div>

        <aside className="inicio__lado" aria-label="Como está indo">
          <div className="postit postit--direita inicio__ofensiva">
            <p className="inicio__ofensiva-numero">
              {p.ofensiva.atual} {p.ofensiva.atual === 1 ? "dia" : "dias"}
            </p>
            <p className="inicio__ofensiva-texto">
              {p.ofensiva.atual === 1 ? "seguido" : "seguidos"}, recorde {p.ofensiva.recorde}
            </p>
          </div>

          <section className="cartao-mao inicio__resumo" aria-labelledby="resumo-titulo">
            <h2 className="t-secao" id="resumo-titulo">
              Resumo
            </h2>
            <dl className="resumo">
              <div className="resumo__linha">
                <dt className="t-ajuda">Certas hoje</dt>
                <dd className="t-secao">
                  {p.acertosHoje} de {p.respondidosHoje}
                </dd>
              </div>
              <div className="resumo__linha">
                <dt className="t-ajuda">No banco</dt>
                <dd className="t-secao">
                  {p.itensVistos} de {p.itensTotal}
                </dd>
              </div>
              <div className="resumo__linha">
                <dt className="t-ajuda">Até a prova</dt>
                <dd className="t-secao">{ateAProva(p.diasAteAlvo)}</dd>
              </div>
              <div className="resumo__linha">
                <dt className="t-ajuda">Acerto no mês</dt>
                <dd className="t-secao">{acerto === null ? "–" : `${acerto}%`}</dd>
              </div>
            </dl>
          </section>

          <section className="inicio__semana" aria-labelledby="semana-titulo">
            <h2 className="t-secao" id="semana-titulo">
              Esta semana
            </h2>
            <Semana painel={p} />
            <ComecoDaConta p={p} />
          </section>

          {p.dosagem.length > 0 && (
            <section className="inicio__dosagem" aria-labelledby="dosagem-titulo">
              <h2 className="t-secao" id="dosagem-titulo">
                Dosagem de hoje
              </h2>
              {agruparPorMateria(p.dosagem, (d) => d.categoria).map((g) => (
                <div key={g.materia ?? "(sem matéria)"}>
                  <p className="t-anotacao-p">{g.materia ?? "(sem matéria)"}</p>
                  <ul>
                    {g.itens.map((d) => {
                      const dentro = abaixoDaMateria(d.categoria);
                      return (
                        <li key={d.categoriaId} className="resumo__linha">
                          <span className="t-ajuda">{dentro.length === 0 ? "a matéria inteira" : dentro.join(" › ")}</span>
                          <span className={d.feitosHoje >= d.alvoDiario ? "t-anotacao t-verde" : "t-anotacao"}>
                            {d.feitosHoje} de {d.alvoDiario}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </section>
          )}

          <Mural />
        </aside>
      </div>
    </div>
  );
}
